import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, ChevronRight } from 'lucide-react';
import { useCompetitions } from '@/hooks/useApi';
import { useThemeImages } from '@/hooks/useThemeImages';

export default function CompetitionsPage() {
  const { t, i18n } = useTranslation();
  const { data: competitions, isLoading } = useCompetitions();
  const themeImages = useThemeImages();

  const genderLabel = (g: string) => {
    const map: Record<string, string> = {
      male: t('competitions.male'),
      female: t('competitions.female'),
      mixed: t('competitions.mixed'),
      open: t('competitions.open'),
    };
    return map[g] || g;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex items-center gap-4 mb-2">
        <h1 className="section-title">{t('competitions.title')}</h1>
        <img src={themeImages.athletes[0]} alt="" className="w-16 h-16 object-contain hidden md:block" />
      </div>
      <p className="section-subtitle">{t('competitions.subtitle')}</p>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-6 bg-bg-secondary rounded w-2/3 mb-3" />
              <div className="h-4 bg-bg-secondary rounded w-full mb-2" />
              <div className="h-4 bg-bg-secondary rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : Array.isArray(competitions) && competitions.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {competitions.map((comp, idx) => (
            <Link key={comp.id} to={`/competitions/${comp.id}`} className="card group">
              <div className="flex items-start justify-between mb-1">
                <h3 className="font-bold text-lg group-hover:text-primary transition-colors">
                  {i18n.language === 'en' ? comp.nameEn : comp.name}
                </h3>
                <img src={themeImages.icons[idx % themeImages.icons.length]} alt="" className="w-10 h-10 object-contain opacity-60 shrink-0" />
              </div>
              <p className="text-text-secondary text-sm line-clamp-2 mb-4">{comp.description}</p>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4 text-text-secondary">
                  <span className="badge text-xs">{genderLabel(comp.gender)}</span>
                  <span>{comp.ageBracket}</span>
                </div>
                <div className="flex items-center gap-1 text-text-secondary">
                  <Users className="w-4 h-4" /> {comp.participants}
                </div>
              </div>
              <div className="mt-3 flex items-center text-primary text-sm font-medium group-hover:gap-2 transition-all">
                {t('common.viewDetails')} <ChevronRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 text-text-secondary">{t('common.noData')}</div>
      )}
    </div>
  );
}
